<template>
    <div class="period-selector">
        <div class="period-presets">
            <button
                v-for="preset in presets"
                :key="preset.key"
                type="button"
                class="period-preset"
                :class="{ 'period-preset--active': activePreset === preset.key }"
                @click="applyPreset(preset)"
            >
                {{ preset.label }}
            </button>
        </div>
        <date-picker
            :value="rangeValue"
            range
            format="DD/MM/YYYY"
            value-type="format"
            :placeholder="$t('statistics.dashboard.select_period')"
            class="period-picker"
            @input="onCustomChange"
        />
    </div>
</template>

<script>
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/locale/fr'
import 'vue2-datepicker/index.css'

function fmt(d) {
    return d.toISOString().slice(0, 10)
}

function isoToFr(iso) {
    if (!iso) return ''
    const [y, m, d] = iso.split('-')
    return `${d}/${m}/${y}`
}

function frToIso(fr) {
    if (!fr) return ''
    const [d, m, y] = fr.split('/')
    return `${y}-${m}-${d}`
}

export default {
    name: 'PeriodSelector',
    components: { DatePicker },
    props: {
        value: { type: Object, default: null }
    },
    computed: {
        presets() {
            return [
                { key: '7d', label: this.$t('statistics.dashboard.preset_7d'), days: 7 },
                { key: '30d', label: this.$t('statistics.dashboard.preset_30d'), days: 30 },
                { key: '90d', label: this.$t('statistics.dashboard.preset_90d'), days: 90 },
                { key: 'ytd', label: this.$t('statistics.dashboard.preset_ytd'), ytd: true }
            ]
        },
        activePreset() {
            if (!this.value) return null
            const today = fmt(new Date())
            if (this.value.to !== today) return null
            for (const p of this.presets) {
                const range = this.computeRange(p)
                if (range.from === this.value.from && range.to === this.value.to) {
                    return p.key
                }
            }
            return null
        },
        rangeValue() {
            if (!this.value || !this.value.from || !this.value.to) return [null, null]
            return [isoToFr(this.value.from), isoToFr(this.value.to)]
        }
    },
    methods: {
        computeRange(preset) {
            const today = new Date()
            const to = fmt(today)
            if (preset.ytd) {
                return { from: `${today.getFullYear()}-01-01`, to }
            }
            const from = new Date(today)
            from.setDate(from.getDate() - (preset.days - 1))
            return { from: fmt(from), to }
        },
        applyPreset(preset) {
            this.$emit('input', this.computeRange(preset))
        },
        onCustomChange(range) {
            if (!range || !range[0] || !range[1]) {
                this.$emit('input', null)
                return
            }
            this.$emit('input', {
                from: frToIso(range[0]),
                to: frToIso(range[1])
            })
        }
    }
}
</script>

<style scoped>
.period-selector {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.period-presets {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px;
    background: var(--v2-surface-1);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
}

.period-preset {
    padding: 5px 10px;
    border: none;
    background: transparent;
    color: var(--v2-text-3);
    font: inherit;
    font-size: 0.78rem;
    font-weight: var(--v2-font-weight-medium);
    cursor: pointer;
    border-radius: var(--v2-radius-sm);
    transition: background var(--v2-transition-fast), color var(--v2-transition-fast);
}

.period-preset:hover {
    color: var(--v2-text-1);
}

.period-preset--active {
    background: var(--v2-surface-0);
    color: var(--v2-text-1);
    box-shadow: var(--v2-shadow-sm);
}

.period-picker {
    width: 220px;
}
</style>
